import type { ReactNode } from "react";
import { Calculator, Compass, Hammer, ReceiptText } from "lucide-react";
import { BrandMark } from "../workspaces/shared/ui";

/**
 * THE FRONT DOOR LAYOUT (spec §88A-7/8). Sign in, sign up, invitations,
 * password resets, the terms prompt, onboarding and the blocked-access
 * screen all share it, so the first minutes with Berrier Reno look like one
 * product: the brand and what it does on one side, one calm task on the
 * other. On a phone the brand panel shrinks to a header strip.
 *
 * The journey copy describes only what exists today.
 */

const JOURNEY = [
  { icon: Compass, title: "Find", text: "Screen properties against what you buy." },
  { icon: Calculator, title: "Analyze", text: "Underwrite a deal before you commit." },
  { icon: Hammer, title: "Renovate", text: "Scope, estimate and bid the work." },
  { icon: ReceiptText, title: "Track", text: "Every cost against the budget." },
];

export function AuthLayout({
  title,
  lede,
  eyebrow,
  children,
  footer,
  wide,
}: {
  title: ReactNode;
  lede?: ReactNode;
  eyebrow?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Onboarding and billing need more room than a single form. */
  wide?: boolean;
}) {
  return (
    <div className="auth-shell">
      <aside className="auth-brand">
        <a className="auth-logo" href="/">
          <BrandMark size={34} />
          <span>Berrier Reno</span>
        </a>
        <div className="auth-brand-body">
          <p className="auth-brand-title">From the first look at a house to the last receipt.</p>
          <ul className="auth-journey">
            {JOURNEY.map(({ icon: Icon, title: step, text }) => (
              <li key={step}>
                <span className="auth-journey-icon" aria-hidden="true">
                  <Icon size={18} />
                </span>
                <span>
                  <strong>{step}</strong>
                  <span>{text}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        <p className="auth-brand-foot">Early Access · Your projects are private to your account.</p>
      </aside>
      <main className="auth-main">
        <AppearancePicker />
        <div className={`auth-card ${wide ? "auth-card-wide" : ""}`}>
          {eyebrow && <p className="auth-eyebrow">{eyebrow}</p>}
          <h1>{title}</h1>
          {lede && <p className="auth-lede">{lede}</p>}
          {children}
        </div>
        {footer && <div className="auth-footer">{footer}</div>}
      </main>
    </div>
  );
}
import { AppearancePicker } from "../appearance/AppearancePicker";
